import type { useAuthStore } from '@common/auth-layer/store/auth.store'

import type { PushRegisterOutcome } from '../types/push-notifications.types'

/**
 * Pure, dependency-free push-notification helpers.
 *
 * Extracted from the composable so browser-capability, gate, session-key, and
 * registration-state logic can be unit-tested in isolation (PR #2797 review:
 * usePushNotifications.ts:29).
 */

/** Mutable registration bookkeeping shared between the auth watch and attemptRegister. */
export interface RegistrationState {
  registeredForCurrentSession: boolean
  lastRegisteredLocationId: string
}

/** WebKit/Safari (non-Chrome, non-Android) lacks Web Push standard support. */
export const WEBKIT_SAFARI_UA = /^((?!chrome|android).)*safari/i

/** True when the current browser supports the Web Push standard (Notification + SW, not Safari). */
export function isWebPushSupportedInBrowser(): boolean {
  if (WEBKIT_SAFARI_UA.test(navigator.userAgent)) return false
  if (!('Notification' in window)) return false
  if (!('serviceWorker' in navigator)) return false
  return true
}

export interface AuthenticatedPushRegistrationGate {
  loggedIn: boolean
  sessionKey: string
  locationId: string
  registeredForCurrentSession: boolean
  lastRegisteredLocationId: string
}

/**
 * True when a push-token registration should be attempted: the user is
 * authenticated with a session key and location, and either hasn't registered
 * this session or the location changed since the last registration.
 */
export function canAttemptAuthenticatedPushRegistration(params: AuthenticatedPushRegistrationGate): boolean {
  const { loggedIn, sessionKey, locationId, registeredForCurrentSession, lastRegisteredLocationId } = params
  if (!loggedIn || !sessionKey || !locationId) return false
  return !(registeredForCurrentSession && lastRegisteredLocationId === locationId)
}

/** Trimmed `sessionUniqueKey` from the auth store, or `undefined` when empty. */
export function resolveSessionKeyForPush(authStore: ReturnType<typeof useAuthStore>): string | undefined {
  const fromStore = authStore.sessionUniqueKey
  return String(fromStore ?? '').trim() || undefined
}

/** True when a cached device token can be reused for a location change (no re-prompt needed). */
export function shouldReuseCachedDeviceFcmToken(
  cachedDeviceFcmToken: string | null,
  locationId: string,
  lastRegisteredLocationId: string
): boolean {
  return Boolean(cachedDeviceFcmToken) && Boolean(lastRegisteredLocationId) && locationId !== lastRegisteredLocationId
}

/** Advance registration bookkeeping after an attempt: pin location on success, allow retry on failure/skip. */
export function updateRegistrationStateAfterOutcome(outcome: PushRegisterOutcome, locationId: string, state: RegistrationState): void {
  if (outcome === 'success') {
    state.lastRegisteredLocationId = locationId
    return
  }
  if (outcome === 'failed' || outcome === 'skipped') {
    state.registeredForCurrentSession = false
  }
}
